import { ArrowUpRight, Globe, Palette, DeviceMobile, GoogleLogo, PaintBrush, Wrench } from "@phosphor-icons/react";
import { Reveal, StaggerGroup, StaggerItem } from "./motion";
import { scrollToId } from "@/lib/scroll";

const CATEGORIES = [
  {
    id: "websites",
    index: "01",
    label: "Websites",
    tagline: "Landing pages to full multi-page builds, with maintenance included on packages.",
    Icon: Globe,
    items: [
      { name: "Website Only", price: "$399", meta: "One-off launch · no maintenance" },
      { name: "Starter Website", price: "$300", meta: "1 page · maintenance included" },
      { name: "Business Website", price: "$600", meta: "2–3 pages · maintenance included", popular: true },
      { name: "Premium Website", price: "$900", meta: "3–5 pages · maintenance included" },
      { name: "Standard Maintenance", price: "$200", per: "/mo", meta: "Ongoing edits, updates & care" },
      { name: "Premium Maintenance", price: "$400", per: "/mo", meta: "Unlimited edits & content help" },
    ],
  },
  {
    id: "print",
    index: "02",
    label: "Print Design",
    tagline: "Business cards, flyers, posters, menus and brochures designed to make you look sharp.",
    Icon: Palette,
    items: [
      { name: "Business Card Design", price: "$75", meta: "For 40 cards" },
      { name: "Flyer Design", price: "$85", meta: "For 40 flyers" },
      { name: "Poster Design", price: "$75", meta: "Custom poster design" },
      { name: "Menu Design", price: "$70", meta: "For 10 menus" },
      { name: "Brochure Design", price: "$85", meta: "For 20 brochures" },
    ],
  },
  {
    id: "content-social",
    index: "03",
    label: "Content & Social",
    tagline: "Content that keeps your brand alive online — from single pieces to full management.",
    Icon: DeviceMobile,
    items: [
      { name: "Content Creation", price: "from $100", meta: "Photo & graphic content" },
      { name: "Social Media Content Packages", price: "from $200", meta: "Batch of posts + stories" },
      { name: "Social Media Management", price: "Custom", meta: "Full-service posting & strategy" },
    ],
  },
  {
    id: "google",
    index: "04",
    label: "Google Setup",
    tagline: "Get found by the right customers on Google, with a beautiful review card to match.",
    Icon: GoogleLogo,
    items: [
      { name: "Google Business Profile Setup", price: "$150", meta: "Complete setup & optimisation" },
      { name: "Google Review QR Code", price: "$30–$35", meta: "Custom-designed review card" },
    ],
  },
  {
    id: "branding",
    index: "05",
    label: "Branding",
    tagline: "Distinctive logos and identity systems that make your business unforgettable.",
    Icon: PaintBrush,
    items: [
      { name: "Logo Design", price: "$150", meta: "Custom logo with revisions" },
      { name: "Basic Branding Package", price: "$250", meta: "Logo + basic brand guidelines" },
      { name: "Custom Designs", price: "from $60", meta: "Anything else you need" },
    ],
  },
];

const EXTRA_COSTS = [
  { label: "Domain Name", val: "$20–$50 / year" },
  { label: "Website Hosting", val: "$20–$60 / month" },
  { label: "Premium Plugins / Software", val: "If required" },
  { label: "Business Email Setup", val: "Upon request" },
];

const PriceCard = ({ item, category }) => (
  <div
    data-testid={`price-${category}-${item.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
    className={`relative rounded-[4px] border p-6 h-full flex flex-col transition-all duration-500 hover:-translate-y-1 ${
      item.popular
        ? "bg-ink text-cream border-ink"
        : "bg-white text-ink border-ink/12 hover:border-gold/50"
    }`}
  >
    {item.popular && (
      <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gold text-ink font-mono text-[9px] uppercase tracking-[0.2em] px-3 py-1 rounded-full whitespace-nowrap">
        Most Popular
      </span>
    )}
    <h4 className={`font-display font-bold text-lg leading-tight ${item.popular ? "text-cream" : "text-ink"}`}>
      {item.name}
    </h4>
    <p className={`text-xs mt-2 mb-5 flex-1 leading-relaxed ${item.popular ? "text-cream/55" : "text-ink/55"}`}>
      {item.meta}
    </p>
    <div className={`pt-4 border-t flex items-baseline gap-1 ${item.popular ? "border-cream/15" : "border-ink/10"}`}>
      <span className="font-display font-extrabold text-3xl tracking-tight">{item.price}</span>
      {item.per && (
        <span className={`font-mono text-xs ${item.popular ? "text-cream/50" : "text-ink/40"}`}>{item.per}</span>
      )}
    </div>
  </div>
);

const Category = ({ cat }) => {
  const Icon = cat.Icon;
  return (
    <div id={`cat-${cat.id}`} className="scroll-mt-28" data-testid={`category-${cat.id}`}>
      <Reveal>
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-8 pb-5 border-b border-ink/10">
          <div className="flex items-start gap-5">
            <span className="font-mono text-sm text-gold-deep mt-1">{cat.index}</span>
            <div>
              <div className="flex items-center gap-3">
                <Icon size={20} weight="regular" className="text-gold-deep" />
                <h3 className="font-display font-bold text-3xl sm:text-4xl tracking-tight">{cat.label}</h3>
              </div>
              <p className="text-ink/55 text-sm mt-2 max-w-lg leading-relaxed">{cat.tagline}</p>
            </div>
          </div>
        </div>
      </Reveal>

      <StaggerGroup className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {cat.items.map((item) => (
          <StaggerItem key={item.name}>
            <PriceCard item={item} category={cat.id} />
          </StaggerItem>
        ))}
      </StaggerGroup>
    </div>
  );
};

export default function Pricing() {
  return (
    <section id="pricing" className="bg-cream text-ink py-24 lg:py-36" data-testid="pricing-section">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
        <Reveal>
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="font-mono text-[11px] uppercase tracking-[0.35em] text-gold-deep">Services &amp; Prices</span>
            <h2 className="font-display font-extrabold tracking-mega leading-[0.98] text-5xl sm:text-6xl lg:text-7xl mt-4">
              Beautifully designed.<br />
              <span className="font-editorial italic font-normal text-gold-deep">Honestly priced.</span>
            </h2>
            <p className="text-ink/55 mt-6 leading-relaxed">
              Websites, print, branding, social and Google — all in one place. Every price below is a starting point.
              Have something bigger in mind? Just ask.
            </p>
          </div>
        </Reveal>

        {/* Category quick-links */}
        <Reveal delay={0.05}>
          <div className="mb-16 flex flex-wrap justify-center gap-2" data-testid="category-quicklinks">
            {CATEGORIES.map((c) => (
              <button
                key={c.id}
                data-testid={`quicklink-${c.id}`}
                onClick={() => scrollToId(`cat-${c.id}`)}
                className="font-mono text-[10px] uppercase tracking-[0.15em] border border-ink/15 rounded-full px-4 py-2 text-ink/70 hover:bg-ink hover:text-cream hover:border-ink transition-colors duration-300"
              >
                <span className="text-gold-deep mr-1.5">{c.index}</span>
                {c.label}
              </button>
            ))}
          </div>
        </Reveal>

        {/* Categories */}
        <div className="space-y-24 lg:space-y-28">
          {CATEGORIES.map((cat) => (
            <Category key={cat.id} cat={cat} />
          ))}
        </div>

        {/* Additional client costs */}
        <Reveal>
          <div className="mt-24 bg-white border border-ink/12 rounded-[4px] p-8 lg:p-10" data-testid="additional-costs">
            <div className="flex items-center gap-3 mb-2">
              <Wrench size={18} className="text-gold-deep" />
              <h3 className="font-display font-bold text-2xl">Additional client costs</h3>
            </div>
            <p className="text-ink/55 text-sm mb-8 max-w-2xl">
              These are generally paid separately unless included in your quote — I&apos;ll always be upfront about what you need.
            </p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {EXTRA_COSTS.map((c) => (
                <div key={c.label} className="border-t border-ink/12 pt-4">
                  <div className="font-display font-bold text-base">{c.label}</div>
                  <div className="font-mono text-sm text-gold-deep mt-1">{c.val}</div>
                </div>
              ))}
            </div>
          </div>
        </Reveal>

        {/* Custom project band */}
        <Reveal>
          <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-6 bg-ink text-cream rounded-[4px] p-8 lg:p-10">
            <div>
              <h3 className="font-display font-bold text-2xl">Something bigger?</h3>
              <p className="text-cream/55 text-sm mt-2 max-w-xl">
                Booking systems, e-commerce, memberships, complete brand overhauls — tell me what you need and I&apos;ll put together a custom quote.
              </p>
            </div>
            <button
              data-testid="pricing-custom-btn"
              onClick={() => scrollToId("contact")}
              className="shrink-0 inline-flex items-center gap-2 bg-gold text-ink font-mono text-[12px] uppercase tracking-[0.14em] px-7 py-4 rounded-full hover:bg-cream transition-colors duration-300"
            >
              Get a Custom Quote <ArrowUpRight weight="bold" size={15} />
            </button>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
